import type { PuzzleBoardView, PuzzleViewModel } from "../phaser/viewModel";
import {
  FOAM_POP_MUSIC_STORAGE_KEY,
  FOAM_POP_MUSIC_TRACKS,
  type FoamMusicCue
} from "./manifest";

export const FOAM_MUSIC_DANGER_ENTER_ROWS = 3;
export const FOAM_MUSIC_DANGER_EXIT_ROWS = 5;

export interface FoamMusicState {
  cue: FoamMusicCue;
  muted: boolean;
  unlocked: boolean;
  suspended: boolean;
  playing: boolean;
}

/**
 * Counts complete hex rows between the deepest projected bubble and the
 * authoritative lower launch line. Pending attacks are included so the warning
 * music arrives before an already-queued payload reaches the firing position.
 */
export function puzzleRowsBeforeLaunchLine(
  board: PuzzleBoardView,
  logicalBoard: PuzzleViewModel["logicalBoard"]
): number {
  if (board.cells.length === 0) return Number.POSITIVE_INFINITY;
  const deepestRow = board.cells.reduce((deepest, cell) => Math.max(deepest, cell.row), 0);
  const deepestBottom = logicalBoard.bubbleRadius * 2 + deepestRow * logicalBoard.hexRowHeight;
  const physicalRows = Math.max(
    0,
    Math.ceil((board.dangerLineY - deepestBottom) / logicalBoard.hexRowHeight)
  );
  const queuedRows = board.pendingPressureRows
    + Math.ceil(board.pendingAttackBubbles / logicalBoard.columns);
  return Math.max(0, physicalRows - queuedRows);
}

/** @deprecated Compatibility name for the original music-policy tests. */
export const puzzleRowsBeforeDanger = puzzleRowsBeforeLaunchLine;

/** Pure policy used by both local and authoritative remote snapshots. */
export function selectPuzzleMusicCue(
  view: PuzzleViewModel,
  currentCue: FoamMusicCue = "gameplay"
): FoamMusicCue {
  if (view.phase !== "playing") return "gameplay";
  // Overtime is always the final pressure phase, even while the board is still
  // far from the launch line. Browser playback still begins only after the
  // normal user-gesture unlock required by iPad/Safari.
  if (view.overtime) return "danger";
  const primary = view.boards.find((board) => board.playerId === view.primaryPlayerId) ?? view.boards[0];
  if (!primary || primary.status !== "active") return "gameplay";
  const rowsRemaining = puzzleRowsBeforeLaunchLine(primary, view.logicalBoard);
  const threshold = currentCue === "danger"
    ? FOAM_MUSIC_DANGER_EXIT_ROWS
    : FOAM_MUSIC_DANGER_ENTER_ROWS;
  return rowsRemaining <= threshold ? "danger" : "gameplay";
}

type MusicListener = (state: FoamMusicState) => void;

/**
 * Page-level music owner. A single HTMLAudioElement survives renderer rebuilds
 * and orientation changes, while all cue decisions remain snapshot-derived.
 */
export class FoamMusicDirector {
  private audio: HTMLAudioElement | null = null;
  private cue: FoamMusicCue = "intro";
  private loadedCue: FoamMusicCue | null = null;
  private muted = this.readStoredMuted();
  private unlocked = false;
  private suspended = typeof document !== "undefined" && document.hidden;
  private playing = false;
  private transition = 0;
  private fadeTimer: number | null = null;
  private fadeResolve: (() => void) | null = null;
  private readonly listeners = new Set<MusicListener>();

  readonly state = (): FoamMusicState => ({
    cue: this.cue,
    muted: this.muted,
    unlocked: this.unlocked,
    suspended: this.suspended,
    playing: this.playing
  });

  subscribe(listener: MusicListener): () => void {
    this.listeners.add(listener);
    listener(this.state());
    return () => this.listeners.delete(listener);
  }

  setCue(cue: FoamMusicCue): void {
    if (cue === this.cue) {
      if (this.loadedCue === null) this.prepareCue(cue);
      return;
    }
    this.cue = cue;
    if (!this.unlocked || this.muted || this.suspended) {
      this.prepareCue(cue);
      this.publish();
      return;
    }
    void this.transitionTo(cue);
  }

  updateForMatch(view: PuzzleViewModel): void {
    this.setCue(selectPuzzleMusicCue(view, this.cue));
  }

  /** Must be called directly from a pointer or keyboard activation on iPad. */
  unlockFromGesture(): void {
    if (this.muted || this.suspended) return;
    const token = ++this.transition;
    const audio = this.prepareCue(this.cue);
    this.cancelFade();
    audio.volume = 0;
    this.unlocked = true;
    this.playing = true;
    this.publish();
    const attempt = audio.play();
    void attempt.then(() => {
      if (token !== this.transition) return;
      this.unlocked = true;
      this.playing = true;
      this.fadeTo(FOAM_POP_MUSIC_TRACKS[this.cue].volume, 360, token);
      this.publish();
    }).catch(() => {
      if (token !== this.transition) return;
      this.unlocked = false;
      this.playing = false;
      this.publish();
    });
  }

  toggleFromGesture(): void {
    if (this.muted || !this.unlocked) {
      this.muted = false;
      this.storeMuted();
      this.unlockFromGesture();
      return;
    }
    this.muted = true;
    this.storeMuted();
    this.stopPlayback();
    this.publish();
  }

  setSuspended(suspended: boolean): void {
    if (suspended === this.suspended) return;
    this.suspended = suspended;
    if (suspended) {
      this.stopPlayback();
    } else if (this.unlocked && !this.muted) {
      this.resumePlayback();
    }
    this.publish();
  }

  destroy(): void {
    ++this.transition;
    this.cancelFade();
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute("src");
      this.audio.load();
    }
    this.audio = null;
    this.loadedCue = null;
    this.playing = false;
    this.listeners.clear();
    this.updateDocumentState();
  }

  private ensureAudio(): HTMLAudioElement {
    if (this.audio) return this.audio;
    const audio = new Audio();
    audio.preload = "auto";
    audio.loop = true;
    audio.volume = 0;
    audio.addEventListener("playing", () => {
      this.playing = true;
      this.publish();
    });
    audio.addEventListener("pause", () => {
      this.playing = false;
      this.publish();
    });
    audio.addEventListener("error", () => {
      this.playing = false;
      this.publish();
    });
    this.audio = audio;
    return audio;
  }

  private prepareCue(cue: FoamMusicCue): HTMLAudioElement {
    const audio = this.ensureAudio();
    if (this.loadedCue === cue) return audio;
    audio.pause();
    audio.src = FOAM_POP_MUSIC_TRACKS[cue].path;
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = 0;
    audio.load();
    this.loadedCue = cue;
    return audio;
  }

  private async transitionTo(cue: FoamMusicCue): Promise<void> {
    const token = ++this.transition;
    const audio = this.ensureAudio();
    this.cancelFade();
    if (this.loadedCue !== null && this.loadedCue !== cue && !audio.paused) {
      await this.fadeTo(0, 180, token);
    }
    if (token !== this.transition || this.muted || this.suspended || cue !== this.cue) return;
    const prepared = this.prepareCue(cue);
    try {
      await prepared.play();
      if (token !== this.transition) return;
      this.unlocked = true;
      this.playing = true;
      this.fadeTo(FOAM_POP_MUSIC_TRACKS[cue].volume, 360, token);
    } catch {
      if (token !== this.transition) return;
      this.unlocked = false;
      this.playing = false;
    }
    this.publish();
  }

  private resumePlayback(): void {
    const token = ++this.transition;
    const audio = this.prepareCue(this.cue);
    const attempt = audio.play();
    void attempt.then(() => {
      if (token !== this.transition) return;
      this.playing = true;
      this.fadeTo(FOAM_POP_MUSIC_TRACKS[this.cue].volume, 280, token);
      this.publish();
    }).catch(() => {
      if (token !== this.transition) return;
      this.unlocked = false;
      this.playing = false;
      this.publish();
    });
  }

  private stopPlayback(): void {
    ++this.transition;
    this.cancelFade();
    if (this.audio) {
      this.audio.volume = 0;
      this.audio.pause();
    }
    this.playing = false;
  }

  private fadeTo(target: number, durationMs: number, token: number): Promise<void> {
    const audio = this.audio;
    if (!audio || token !== this.transition) return Promise.resolve();
    this.cancelFade();
    const start = audio.volume;
    const startedAt = performance.now();
    return new Promise((resolve) => {
      this.fadeResolve = resolve;
      const step = (): void => {
        if (!this.audio || token !== this.transition) {
          this.cancelFade();
          return;
        }
        const elapsed = performance.now() - startedAt;
        const progress = Math.min(1, elapsed / Math.max(1, durationMs));
        this.audio.volume = start + (target - start) * progress;
        if (progress >= 1) {
          this.cancelFade();
          return;
        }
        this.fadeTimer = window.setTimeout(step, 32);
      };
      step();
    });
  }

  private cancelFade(): void {
    if (this.fadeTimer !== null) window.clearTimeout(this.fadeTimer);
    this.fadeTimer = null;
    const resolve = this.fadeResolve;
    this.fadeResolve = null;
    resolve?.();
  }

  private readStoredMuted(): boolean {
    if (typeof localStorage === "undefined") return false;
    try {
      return localStorage.getItem(FOAM_POP_MUSIC_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }

  private storeMuted(): void {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(FOAM_POP_MUSIC_STORAGE_KEY, String(this.muted));
    } catch {
      // Storage can be unavailable in private browsing; runtime audio still works.
    }
  }

  private publish(): void {
    this.updateDocumentState();
    const state = this.state();
    for (const listener of this.listeners) listener(state);
  }

  private updateDocumentState(): void {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.foamMusicCue = this.cue;
    document.documentElement.dataset.foamMusicPlaying = String(this.playing && !this.muted && !this.suspended);
  }
}
